/**
 * Goal Score PR-7b — Studio authoring types for the new "Goal" concept.
 *
 * Vocabulary (docs/goal_score/F_ui_ux_designs.md §1, applied EVERYWHERE a
 * new string is added in this PR): the six existing checkpoint types
 * (`movement_goal`, `axis_goal`, `joint_angle_goal`, `timing_goal`, etc.)
 * are called "Checks" in the UI. "Goal" is reserved for the NEW competitive
 * target this file's types describe — a Distance/Speed/Angle bar an
 * athlete can beat for bonus points, feeding the uncapped Total score.
 * Never call one of these a "goal" in lowercase prose without the capital-G
 * sense, and never call the old six types "Goals" in new copy.
 *
 * `TravelTarget` already exists in `@/types/trajectory.ts` (travel-distance
 * plan v2.1, PR-A) — byte-identical to
 * `backend/app/models/spatial_goals.py::TravelTarget`. This file adds its
 * two new siblings, `SpeedTarget` (PR-4) and `AngleTarget` (PR-5), plus the
 * Form-effect curve shape (PR-2, `backend/app/models/form_effect.py`) and
 * the request/response shapes for the three new/updated preview routes
 * this PR wires into the Studio. Field names are byte-identical to their
 * backend Pydantic counterparts — do not rename on either side without
 * updating both and re-running
 * `backend/tests/test_goals_preview_generalized_route.py` /
 * `test_form_effect_curve_preview_route.py`.
 *
 * NOTE on the opt-in shape: there is NO `counts_toward_goal_score` field
 * anywhere in the final backend contract (an earlier draft had one; it was
 * removed in PR-5's pre-red-team fix because scoring never read it). The
 * opt-in IS the shape itself:
 *   - Distance Goal:  `movement_goal.travel_target` is set.
 *   - Speed Goal:     `movement_goal.speed_target` is set.
 *   - Angle Goal:      `joint_angle_goal.aggregation === 'p95_max'` (or
 *                      `axis_goal.aggregation === 'p95_max'`) WITH
 *                      `angle_goal_target` set.
 * Do not invent a boolean opt-in flag on the frontend either.
 */

// `TravelTarget`/`SpeedTarget`/`AngleTarget` and their comparison-mode enums
// live in `./trajectory.ts` (co-located with `TravelTarget`, which predates
// this PR) — re-exported here so every other Goal-authoring file can import
// them from one place without reaching into `trajectory.ts` directly.
import type {
  TravelTarget,
  TravelTargetSource,
  BodyHeightDefinition,
  SpeedTarget,
  SpeedComparisonMode,
  AngleTarget,
  AngleGoalComparison,
} from './trajectory';

export type {
  TravelTarget,
  TravelTargetSource,
  BodyHeightDefinition,
  SpeedTarget,
  SpeedComparisonMode,
  AngleTarget,
  AngleGoalComparison,
};

/** The three Goal kinds a coach can author (F §2 step 1: "Pick a kind"). */
export type GoalKind = 'distance' | 'speed' | 'angle';

// ---------------------------------------------------------------------------
// Form-effect curve (Goal Score PR-2). Byte-identical to
// backend/app/models/form_effect.py::FormEffectAnchor / FormEffectCurve.
// ---------------------------------------------------------------------------

export interface FormEffectAnchor {
  /** 0-100. */
  form: number;
  /** >= 0. */
  multiplier: number;
}

export interface FormEffectCurve {
  /** 2-10 points, Form strictly increasing, multiplier non-decreasing. */
  anchors: FormEffectAnchor[];
  /** (0, 5.0]. Default 2.0. */
  max_multiplier: number;
}

/** The provenanced default a NEW template's curve editor prefills with —
 * P25/P50/P75 Form percentiles from the EXP-007 60-row corpus, rounded to
 * 1dp (`backend/app/models/form_effect.py::DEFAULT_FORM_EFFECT_ANCHORS`).
 * This is NOT the scoring default for an unset curve (that's Neutral,
 * below) — H2 §6.1: "Neutral ships as the template default" means the
 * SCORING default; a coach who opens this control sees the percentile
 * curve prefilled, and ships it unless they edit it. */
export const DEFAULT_FORM_EFFECT_CURVE: FormEffectCurve = {
  anchors: [
    { form: 65.7, multiplier: 0.5 },
    { form: 74.7, multiplier: 1.0 },
    { form: 85.0, multiplier: 2.0 },
  ],
  max_multiplier: 2.0,
};

/** "Use the standard setting (Form doesn't change the bonus)" — one click
 * sets this (H2 §5 item 4). */
export const NEUTRAL_FORM_EFFECT_CURVE: FormEffectCurve = {
  anchors: [
    { form: 0.0, multiplier: 1.0 },
    { form: 100.0, multiplier: 1.0 },
  ],
  max_multiplier: 2.0,
};

export const FORM_EFFECT_MIN_ANCHORS = 2;
export const FORM_EFFECT_MAX_ANCHORS = 10;
export const FORM_EFFECT_MAX_MULTIPLIER_CEILING = 5.0;

// ---------------------------------------------------------------------------
// POST /templates/{id}/goals/preview (PR-3 generalized; PR-4 adds "speed";
// PR-5 adds "angle"). Byte-identical to
// backend/app/api/template_routes.py::GoalsPreviewRequest and the three
// response shapes (TravelPreviewResponse/SpeedGoalPreviewResponse/
// AnglePreviewResponse) — NO shared response_model on the backend route
// (a Union would silently drop Speed/Angle-only fields), so this file
// mirrors that with three distinct response types, never one unioned type
// naively validated.
// ---------------------------------------------------------------------------

export interface GoalsPreviewRequest {
  kind: GoalKind;
  /** Distance/Speed only: the tracked landmark. */
  landmark?: string | null;
  /** Angle only, joint_angle_goal family: the named joint. */
  joint_name?: string | null;
  /** Angle only, axis_goal family: the axis start/end landmarks. */
  landmark_start?: string | null;
  landmark_end?: string | null;
  target_phase?: string | null;
  body_height_definition?: BodyHeightDefinition | null;
}

export interface DistanceGoalPreviewResponse {
  ratio: number | null;
  body_height: number | null;
  definition_used: BodyHeightDefinition | null;
  frames_used: number;
  status: 'computed' | 'abstained';
  reason: string | null;
  trajectory_points: Array<[number, number, number]> | null;
}

export interface SpeedGoalPreviewResponse {
  speed_body_heights_per_s: number | null;
  body_height: number | null;
  definition_used: BodyHeightDefinition | null;
  duration_s: number | null;
  frames_used: number;
  status: 'computed' | 'abstained';
  reason: string | null;
}

export interface AngleGoalPreviewResponse {
  p95_degrees: number | null;
  raw_max_degrees: number | null;
  frames_used: number;
  status: 'computed' | 'abstained';
  reason: string | null;
}

// ---------------------------------------------------------------------------
// POST /templates/{id}/form-effect-curve/preview (PR-2, shipped dark;
// wired into the Studio by this PR). Byte-identical to
// FormEffectCurvePreviewRequest/Response in template_routes.py.
// ---------------------------------------------------------------------------

export interface FormEffectCurvePreviewRequest {
  curve: FormEffectCurve;
  /** 1-1001 Form values to sample. */
  forms: number[];
}

export interface FormEffectCurvePreviewResponse {
  /** `[form, multiplier]` pairs, backend-computed. */
  samples: Array<[number, number]>;
}

// ---------------------------------------------------------------------------
// POST /templates/{id}/calibration-takes/{take_id}/measure-goals (NEW this
// PR, G §2.3a / K PR-7). Recompute-fresh, never cached, never persisted.
// Byte-identical to backend/app/api/calibration_take_routes.py's
// CalibrationTakeGoalMeasurementResponse / CalibrationTakeMeasureGoalsResponse
// (which mirror app/services/calibration_take_goal_measurement.py's
// GoalMeasurement dataclass) — do not rename a field here without updating
// both sides and re-running test_calibration_take_goal_measurement.py.
// ---------------------------------------------------------------------------

/** The RAW per-checkpoint observability status (never "exceeded"/"missed" —
 * see `deriveGoalMeasurementDisplayStatus` below for the four-state UI
 * label mockup 04 shows). Matches `goal_observability.GoalStatus`. */
export type RawGoalMeasurementStatus = 'computed' | 'degraded' | 'abstained';

/** One configured Goal's measurement against one calibration take — a
 * strict mirror of `calibration_take_goal_measurement.GoalMeasurement`.
 * `bonus`/`score` are the SAME values `goal_scoring._project_goal` would
 * produce for a scored evaluation's `goals[]` entry — never recomputed
 * independently here. */
export interface TakeGoalMeasurement {
  goal_key: string;
  kind: GoalKind;
  /** The template annotation id this Goal lives on. */
  annotation_id: string | null;
  /** Coach-facing label for the checkpoint, when the template has one. */
  label: string | null;
  target_phase: string | null;
  measured: number | null;
  target: number | null;
  comparison: string | null;
  importance: string | null;
  /** `null` = not measured/not bonus-eligible. `0.0` = measured, met or
   * fell short (Form already gave it full credit). `> 0` = exceeded —
   * LFT-063/B1's `0.0 != None` rule applies here exactly as it does on the
   * scored-evaluation path. */
  bonus: number | null;
  status: RawGoalMeasurementStatus;
  /** The checkpoint's own 0-100 Form-side score (Gaussian k=1) — used,
   * together with `bonus`, to derive the four-state display status. */
  score: number | null;
  /** Present iff status !== 'computed' — already coach-facing plain
   * language (never the raw backend diagnostic string), safe to render
   * directly. */
  abstention_reason: string | null;
  degrade_reason: string | null;
}

export interface TakeMeasureGoalsResponse {
  take_id: string;
  template_id: string;
  /** Empty when the template has no configured Goals — distinct from "all
   * abstained" (a non-empty list where every entry's status is
   * 'abstained'). */
  goals: TakeGoalMeasurement[];
}

/** The mockup's four-state display status (F §5 / §3's per-Goal card
 * states), derived client-side from the raw measurement — the backend
 * deliberately returns the raw scoring primitives (`bonus`/`score`/
 * `status`), not a pre-baked UI label, so this derivation lives in exactly
 * one place rather than drifting between a backend copy and a frontend
 * copy. */
export type GoalMeasurementDisplayStatus = 'exceeded' | 'met' | 'missed' | 'abstained';

/**
 * `GOAL_PASS_THRESHOLD` mirrors `backend/app/config/scoring_config.py`'s
 * `get_goal_pass_threshold()` (`GoalScoreConfig.pass_threshold`, 70.0) — the
 * SAME duplicate-a-scoring-constant-client-side pattern
 * `travelDistanceCopy.ts`'s `TRAVEL_TOLERANCE_MIN_FRACTION_OF_TARGET`
 * already uses, for the same reason: no live endpoint returns this number
 * at authoring/preview time. If that backend default ever changes, this
 * must change with it.
 */
export const GOAL_PASS_THRESHOLD = 70.0;

export function deriveGoalMeasurementDisplayStatus(
  m: Pick<TakeGoalMeasurement, 'status' | 'bonus' | 'score'>,
): GoalMeasurementDisplayStatus {
  if (m.status !== 'computed') return 'abstained';
  if (m.bonus === null || m.bonus === undefined) return 'abstained';
  if (m.bonus > 0) return 'exceeded';
  if (m.score !== null && m.score !== undefined && m.score >= GOAL_PASS_THRESHOLD) return 'met';
  return 'missed';
}
